import { Plus } from 'lucide-react'
import { useState } from 'react'

import Button from '@/elements/Button'
import Flex from '@/elements/Flex'
import Text from '@/elements/Text'
import Table from '@/components/Table'
import Modal from '@/components/Modal'
import useBreakpoints from '@/hooks/useBreakpoints'
import useAuthStore from '@/stores/authStore'
import useRoles from '@/hooks/useRoles'
import { IS_MOBILE_APP } from '@/constants'
import { OrganizationMemberRoles } from '@/api/types/Organization'
import { useGetOrgUserInfo } from '@/api/user'
import { useDeviceTokensTable } from '@/hooks/tables/deviceTokensTable'
import Loading from '@/elements/Loading'
import IndividualNotificationsPreferences from '../IndividualNotificationsPreferences'
import MemberNotificationsPreferences from '../MemberNotificationsPreferences'
import usePushNotifications from '@/hooks/usePushNotifications'

function SettingsNotifications() {
  const { isTablet, isMobile } = useBreakpoints()
  const { user } = useAuthStore()
  const { getRoleName, isInnerCircle } = useRoles()

  const {
    devices,
    loading,
    myDeviceToken,
    myDeviceIsRegistered,
    addNewDeviceToken,
    handleRemoveDeviceToken
  } = usePushNotifications({
    afterDelete: () => setShowDeleteModal(false),
  })

  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deviceTokenToDelete, setDeviceTokenToDelete] = useState<string>('')
  const deviceTokensTableData = useDeviceTokensTable(
    devices,
    myDeviceToken,
    (deviceTokenId: string) => {
      setDeviceTokenToDelete(deviceTokenId)
      setShowDeleteModal(true)
    }
  )

  // Notifications for current user for all roles on current organization
  const { data: orgUserInfo } = useGetOrgUserInfo({ userId: user!.id }, !isInnerCircle)
  const orgIndividualId = orgUserInfo?.data?.organizationIndividual?.id || ''
  const orgMembersId = orgUserInfo?.data?.organizationMembers?.sort(
    (a, b) => OrganizationMemberRoles.indexOf(a.role?.type) - OrganizationMemberRoles.indexOf(b.role?.type)
  ).map(member => member.id)[0] || ''
  const orgMemberRole = orgUserInfo?.data?.organizationMembers?.find(member => member.id === orgMembersId)?.role?.type
  const orgMemberRoleName = orgMemberRole ? getRoleName(orgMemberRole) : ''

  return (
    <Flex className='card' wfull>
      <Flex
        wrap
        grow
        wfull
        inline={!isTablet}
        gap={7}
        p={isMobile ? 3 : isTablet ? 5 : 7}
      >
        <Flex fraction={4} gap={2} wfull>
          <Text variant='h3'>
            Devices with Push Notifications
          </Text>
          <Text variant='h4' color='black-light-40'>
            Manage your devices that receive push notifications.
          </Text>
        </Flex>

        <Flex fraction={6} wfull>
          {loading ? (
            <Loading />
          ) : devices.length > 0 ? (
            <Flex gap={3} wfull>
              <Table {...deviceTokensTableData} />
              <Modal
                isOpen={showDeleteModal}
                title='Remove Device'
                onSave={() => handleRemoveDeviceToken(deviceTokenToDelete)}
                onCancel={() => setShowDeleteModal(false)}
                cancelLabel='No'
                saveLabel='Yes'
              >
                <Text color='black' style={{ hyphens: 'none' }}>
                  Are you sure you want to remove this device from receiving push notifications? This action cannot be undone.
                </Text>
              </Modal>
            </Flex>
          ) : (
            <Text variant='h4' color='black-light-40'>
              No devices registered for push notifications.
            </Text>
          )}

          {IS_MOBILE_APP && (
            <Flex wfull align={isMobile ? 'center' : 'flex-end'} my={3}>
              <Button
                variant='primary'
                onClick={addNewDeviceToken}
                disabled={loading || myDeviceIsRegistered}
                fullWidth={isMobile}
              >
                <Plus size={18} style={{ minWidth: 18 }} />
                Add this device
              </Button>
            </Flex>
          )}
        </Flex>
      </Flex>

      <Flex
        wrap
        grow
        wfull
        inline={!isTablet}
        gap={7}
        p={isMobile ? 3 : isTablet ? 5 : 7}
      >
        <Flex fraction={4} gap={2} wfull>
          <Text variant='h3'>
            Notifications Preferences
          </Text>
          <Text variant='h4' color='black-light-40'>
            Choose how you want to be notified about important alerts.
          </Text>
        </Flex>

        <Flex fraction={6}>
          {orgIndividualId && (
            <Flex wfull pb={8}>
              <Text variant='h3' style={{ paddingBottom: 16 }}>
                Your settings as an Individual
              </Text>
              <Flex fraction={6} wfull>
                <IndividualNotificationsPreferences
                  organizationIndividualId={orgIndividualId}
                />
              </Flex>
            </Flex>
          )}

          {orgMembersId && (
            <Flex wfull pb={8}>
              <Text variant='h3' style={{ paddingBottom: 16 }}>
                Your settings as {orgMemberRoleName}
              </Text>
              <Flex fraction={6} wfull>
                <MemberNotificationsPreferences
                  organizationMemberId={orgMembersId}
                />
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  )
}

export default SettingsNotifications
